/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { gettingStartedContent } from "../Sections/GettingStarted";
import { inputNodesContent } from "../Sections/InputNodes";
import { transformNodesContent } from "../Sections/TransformNodes";
import { outputNodesContent } from "../Sections/OutputNodes";
import { savingLoadingContent } from "../Sections/SavingLoading";
import { canvasControlsContent } from "../Sections/CanvasControls";
import { keyboardShortcutsContent } from "../Sections/KeyboardShortcuts";
import { troubleshootingContent } from "../Sections/Troubleshooting";
import { transformEngineContent } from "../Sections/TransformEngine";
import { initializationModalContent } from "../Sections/Initialization";
import { creatingTransformContent } from "../Sections/CreatingTransform";
import { filesPageContent } from "../Sections/FilesPage";
import { settingsContent } from "../Sections/SettingsPage";
import { pluginsContent } from "../Sections/PluginsPage";
import { installationContent } from "../Sections/Installation";
import { composerNodesContent } from "../Sections/ComposerNodes";
import { quickFormContent } from "../Sections/QuickForm";

export interface DocSubsection {
  id: string;
  title: string;
}

export interface DocSection {
  id: string;
  title: string;
  content: string;
  subsections?: DocSubsection[];
}

/**
 * Documentation sections configuration.
 *
 * To add a new section:
 * 1. Create a new file in ./Sections/ with your content (export as a string)
 * 2. Import it at the top of this file
 * 3. Add a new entry to the docSections array below
 */
export const docSections: DocSection[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    content: gettingStartedContent,
    subsections: [
      { id: "what-is-dexter", title: "What is Dexter?" },
      { id: "key-concepts", title: "Key Concepts" },
      { id: "quick-start", title: "Quick Start" },
    ],
  },
  {
    id: "input-nodes",
    title: "Input Nodes",
    content: inputNodesContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "anatomy", title: "Anatomy" },
      { id: "adding-input-nodes", title: "Adding Input Nodes" },
      { id: "supported-file-types", title: "Supported File Types" },
      { id: "input-node-properties", title: "Input Node Properties" },
      { id: "best-practices", title: "Best Practices" },
    ],
  },
  {
    id: "transform-nodes",
    title: "Transform Nodes",
    content: transformNodesContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "anatomy", title: "Anatomy" },
      {
        id: "transform-families-types",
        title: "Transform Families & Types",
      },
      { id: "supported-transforms", title: "Supported Transforms" },
      { id: "creating-transforms", title: "Creating Transforms" },
      { id: "transform-validation", title: "Transform Validation" },
      { id: "troubleshooting-transforms", title: "Troubleshooting Transforms" },
    ],
  },
  {
    id: "output-nodes",
    title: "Output Nodes",
    content: outputNodesContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "anatomy", title: "Anatomy" },
      { id: "adding-output-nodes", title: "Adding Output Nodes" },
      { id: "downloading-outputs", title: "Downloading Outputs" },
      { id: "batch-download", title: "Batch Download" },
      { id: "building-outputs", title: "Building Outputs" },
      { id: "output-file-names", title: "Output File Names" },
      { id: "managing-outputs", title: "Managing Outputs" },
    ],
  },
  {
    id: "composer-nodes",
    title: "Composer Nodes",
    content: composerNodesContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "anatomy", title: "Anatomy" },
      { id: "adding-composer-nodes", title: "Adding Composer Nodes" },
      { id: "downloading-outputs", title: "Downloading Outputs" },
      { id: "previewing-outputs", title: "Previewing Outputs" },
      {
        id: "customizing-composer-node-outputs",
        title: "Customizing Composer Node Outputs",
      },
      { id: "output-file-names", title: "Output File Names" },
      { id: "managing-outputs", title: "Managing Outputs" },
    ],
  },
  {
    id: "transform-engine",
    title: "Transform Engine",
    content: transformEngineContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "interface-overview", title: "Interface Overview" },
      { id: "toolbar-components", title: "Toolbar Components" },
      { id: "control-bar", title: "Control Bar" },
      { id: "working-with-mappings", title: "Working with Mappings" },
      { id: "best-practices", title: "Best Practices" },
    ],
  },
  {
    id: "initialization-modal",
    title: "Initializing Transform Engine",
    content: initializationModalContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "when-it-appears", title: "When It Appears" },
      { id: "upload-files-and-recommend-transform", title: "Upload Files" },
      {
        id: "select-a-previous-transform-session",
        title: "Select a Previous Transform Session",
      },
      { id: "best-practices", title: "Best Practices" },
    ],
  },
  {
    id: "creating-transform",
    title: "Creating a Transform",
    content: creatingTransformContent,
    subsections: [
      { id: "summary", title: "Summary" },
      {
        id: "workflow-1-initialization-modal",
        title: "Workflow 1: Initialization Modal",
      },
      {
        id: "workflow-2-inputs-side-panel",
        title: "Workflow 2: Inputs Side Panel",
      },
      { id: "comparing-the-two-workflows", title: "Comparing Workflows" },
    ],
  },
  {
    id: "saving-loading",
    title: "Saving & Loading Projects",
    content: savingLoadingContent,
    subsections: [
      { id: "saving-your-work", title: "Saving Your Work" },
      { id: "loading-projects", title: "Loading Projects" },
      { id: "export-and-import", title: "Export and Import" },
    ],
  },
  {
    id: "quickform",
    title: "QuickForm",
    content: quickFormContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "quickform-overview", title: "QuickForm Overview" },
      {
        id: "file-upload-and-deletion",
        title: "File Upload and Deletion",
      },
      {
        id: "transform-results-download",
        title: "Transform Results Download",
      },
      { id: "troubleshooting-tips", title: "Troubleshooting Tips" },
    ],
  },
  {
    id: "files-page",
    title: "Files",
    content: filesPageContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "files-tab-overview", title: "Overview" },
      {
        id: "understanding-files-vs-collections",
        title: "Files vs Collections",
      },
      { id: "searching-for-files", title: "Searching" },
      { id: "sorting-the-file-list", title: "Sorting" },
      { id: "viewing-file-details", title: "File Details" },
    ],
  },
  {
    id: "settings-page",
    title: "Settings",
    content: settingsContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "changing-your-color-scheme", title: "Color Scheme" },
      { id: "coming-soon", title: "Coming Soon" },
    ],
  },
  {
    id: "plugins",
    title: "Plugins",
    content: pluginsContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "what-are-plugins", title: "What Are Plugins?" },
      { id: "current-plugin-support", title: "Current Plugins" },
      { id: "future-plugin-development", title: "Future Development" },
    ],
  },
  {
    id: "canvas-controls",
    title: "Canvas Controls",
    content: canvasControlsContent,
    subsections: [
      { id: "navigation", title: "Navigation" },
      { id: "selecting-nodes", title: "Selecting Nodes" },
      { id: "creating-connections", title: "Creating Connections" },
      { id: "deleting-items", title: "Deleting Items" },
    ],
  },
  {
    id: "keyboard-shortcuts",
    title: "Keyboard Shortcuts",
    content: keyboardShortcutsContent,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    content: troubleshootingContent,
    subsections: [
      { id: "connection-wont-create", title: "Connection Won't Create" },
      { id: "transform-not-processing", title: "Transform Not Processing" },
      { id: "file-upload-fails", title: "File Upload Fails" },
      { id: "project-wont-load", title: "Project Won't Load" },
      { id: "performance-issues", title: "Performance Issues" },
    ],
  },
  {
    id: "installation",
    title: "Installation",
    content: installationContent,
    subsections: [
      { id: "summary", title: "Summary" },
      { id: "prerequisites", title: "Prerequisites" },
      { id: "installation-overview", title: "Overview" },
    ],
  },
];

const subsectionsContainText = (
  searchValue: string,
  subsections?: DocSubsection[]
): boolean => {
  return (
    subsections &&
    subsections.filter((ss) => ss.title.toLowerCase().includes(searchValue))
      .length > 0
  );
};

export const filterDocSections = (searchValue: string): DocSection[] => {
  const sanitizedSearchValue = searchValue.trim().toLowerCase();
  if (sanitizedSearchValue == "") return docSections;
  const firstPass = docSections.filter(
    (section) =>
      section.title.toLowerCase().includes(sanitizedSearchValue) ||
      subsectionsContainText(sanitizedSearchValue, section.subsections)
  );
  return firstPass.map((section) => {
    if (section.title.toLowerCase().includes(sanitizedSearchValue))
      return section;
    else
      return {
        ...section,
        subsections: section.subsections?.filter((ss) =>
          ss.title.toLowerCase().includes(sanitizedSearchValue)
        ),
      };
  });
};
